/**
 * ContentEditable Utilities für Rich-Text mit Asset-Badges.
 *
 * Handles:
 * - Selection/Range API
 * - Badge Insertion
 * - HTML zu Text + Asset-IDs Parsing
 */

export interface AssetBadgeData {
  assetId: string;
  assetName: string;
  assetType: 'logo' | 'style_image' | 'reference_image';
  thumbnailUrl: string;
}

export interface ParsedContent {
  plainText: string;
  assetIds: string[];
}

/**
 * Fügt Asset-Badge an aktueller Cursor-Position ein.
 * WICHTIG: Entfernt das @ Zeichen VOR dem Badge!
 */
export function insertAssetBadge(
  editorElement: HTMLElement,
  badgeData: AssetBadgeData
): void {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;

  const range = selection.getRangeAt(0);

  // Find and delete the @ character before cursor
  const preCaretRange = range.cloneRange();
  preCaretRange.selectNodeContents(editorElement);
  preCaretRange.setEnd(range.endContainer, range.endOffset);
  const textBeforeCursor = preCaretRange.toString();

  // If @ is the last character, delete it
  if (textBeforeCursor.endsWith('@')) {
    // Move start back by 1 to include @
    const textNode = range.endContainer;
    if (textNode.nodeType === Node.TEXT_NODE && range.endOffset > 0) {
      range.setStart(textNode, range.endOffset - 1);
      range.deleteContents();
    }
  }

  // Create badge span
  const badge = createBadgeElement(badgeData);
  range.insertNode(badge);

  // Add space after badge
  const space = document.createTextNode('\u00A0'); // Non-breaking space
  badge.parentNode?.insertBefore(space, badge.nextSibling);

  // Move cursor after space
  range.setStartAfter(space);
  range.setEndAfter(space);
  selection.removeAllRanges();
  selection.addRange(range);

  editorElement.focus();
}

/**
 * Erstellt Badge DOM-Element (safe DOM API, kein innerHTML mit User-Daten).
 */
export function createSafeBadgeElement(badgeData: AssetBadgeData): HTMLSpanElement {
  return createBadgeElement(badgeData);
}

function createBadgeElement(badgeData: AssetBadgeData): HTMLSpanElement {
  const badge = document.createElement('span');
  badge.className = 'asset-badge';
  badge.contentEditable = 'false'; // ← Non-editable!
  badge.dataset.assetId = badgeData.assetId;
  badge.dataset.assetType = badgeData.assetType;

  // Thumbnail
  const img = document.createElement('img');
  img.src = badgeData.thumbnailUrl;
  img.className = 'asset-badge-thumb';
  img.alt = badgeData.assetName;

  // Text
  const text = document.createElement('span');
  text.className = 'asset-badge-text';
  text.textContent = badgeData.assetName;

  // Dropdown icon
  const icon = document.createElement('span');
  icon.className = 'asset-badge-icon';
  icon.innerHTML = '▼';

  badge.appendChild(img);
  badge.appendChild(text);
  badge.appendChild(icon);

  return badge;
}

/**
 * Extrahiert Plain-Text und Asset-IDs aus ContentEditable.
 */
export function parseContentEditable(editorElement: HTMLElement): ParsedContent {
  const assetIds: string[] = [];
  let plainText = '';

  // Clone element to avoid modifying original
  const clone = editorElement.cloneNode(true) as HTMLElement;

  // Replace badges with placeholder text
  const badges = clone.querySelectorAll('.asset-badge');
  badges.forEach((badge) => {
    const assetId = (badge as HTMLElement).dataset.assetId;
    const assetName = badge.querySelector('.asset-badge-text')?.textContent || 'Asset';

    if (assetId) {
      assetIds.push(assetId);
    }

    // Replace with readable text (for Gemini prompt)
    const placeholder = document.createTextNode(`[${assetName}]`);
    badge.parentNode?.replaceChild(placeholder, badge);
  });

  plainText = clone.textContent || '';

  return {
    plainText: plainText.trim(),
    assetIds,
  };
}

/**
 * Fügt Plain-Text an Cursor-Position ein (löscht @ davor).
 */
export function insertTextAtCursor(editorElement: HTMLElement, text: string): void {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;

  const range = selection.getRangeAt(0);
  const preCaretRange = range.cloneRange();
  preCaretRange.selectNodeContents(editorElement);
  preCaretRange.setEnd(range.endContainer, range.endOffset);

  if (preCaretRange.toString().endsWith('@')) {
    const node = range.endContainer;
    if (node.nodeType === Node.TEXT_NODE && range.endOffset > 0) {
      range.setStart(node, range.endOffset - 1);
      range.deleteContents();
    }
  }

  const textNode = document.createTextNode(text + '\u00A0');
  range.insertNode(textNode);
  range.setStartAfter(textNode);
  range.setEndAfter(textNode);
  selection.removeAllRanges();
  selection.addRange(range);
  editorElement.focus();
}

/**
 * Löscht Badge an aktueller Cursor-Position (Backspace/Delete).
 */
export function deleteBadgeAtCursor(_editorElement: HTMLElement): boolean {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return false;

  const range = selection.getRangeAt(0);

  // Check if cursor is after badge (Backspace)
  const prevNode = range.startContainer.childNodes[range.startOffset - 1];
  if (prevNode?.nodeName === 'SPAN' &&
      (prevNode as HTMLElement).classList.contains('asset-badge')) {
    prevNode.parentNode?.removeChild(prevNode);
    return true;
  }

  // Check if cursor is before badge (Delete)
  const nextNode = range.startContainer.childNodes[range.startOffset];
  if (nextNode?.nodeName === 'SPAN' &&
      (nextNode as HTMLElement).classList.contains('asset-badge')) {
    nextNode.parentNode?.removeChild(nextNode);
    return true;
  }

  return false;
}
